import React, { useState } from "react";
import axios from "axios";
import { X, Plus, Minus, Trash2, ArrowUpRight, Check, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import useRazorpayScript from "@/hooks/useRazorpayScript";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
    customer_name: "",
    email: "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "India",
    notes: "",
};

const CartDrawer = () => {
    const { items, open, setOpen, updateQuantity, removeItem, subtotal, discount, total, coupon, couponApplied, clearCart } = useCart();
    const [stage, setStage] = useState("cart");
    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);
    const [orderNumber, setOrderNumber] = useState("");
    const [error, setError] = useState("");
    const razorpayReady = useRazorpayScript();

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const close = () => {
        setOpen(false);
        setTimeout(() => {
            if (stage === "done") {
                setStage("cart");
                setForm(initial);
                setOrderNumber("");
            }
        }, 300);
    };

    const payWithRazorpay = async (e) => {
        if (e) e.preventDefault();
        if (!razorpayReady || !window.Razorpay) {
            toast.error("Payment module still loading — please wait a moment and try again.");
            return;
        }
        setSubmitting(true);
        setError("");

        const payload = {
            ...form,
            items: items.map((it) => ({
                product_id: it.product_id,
                product_name: it.product_name,
                pack_size: it.pack_size,
                unit_price: it.unit_price,
                quantity: it.quantity,
            })),
            subtotal,
            coupon_code: couponApplied && discount > 0 ? coupon.code : "",
            discount,
        };

        let orderRes;
        try {
            const { data } = await axios.post(`${API}/razorpay/create-order`, payload);
            orderRes = data;
        } catch (err) {
            setSubmitting(false);
            const msg =
                err?.response?.data?.detail?.[0]?.msg ||
                err?.response?.data?.detail ||
                "Could not start payment. Please verify your details.";
            setError(msg);
            return;
        }

        const options = {
            key: orderRes.key_id,
            amount: orderRes.amount,
            currency: orderRes.currency,
            name: "Ostrich Foods",
            description: `Order ${orderRes.order_number}`,
            image: "/logo.jpg",
            order_id: orderRes.razorpay_order_id,
            prefill: orderRes.prefill,
            notes: { order_number: orderRes.order_number },
            theme: { color: "#F4571F" },
            handler: async (response) => {
                try {
                    await axios.post(`${API}/razorpay/verify`, {
                        razorpay_order_id: response.razorpay_order_id,
                        razorpay_payment_id: response.razorpay_payment_id,
                        razorpay_signature: response.razorpay_signature,
                        internal_order_id: orderRes.internal_order_id,
                    });
                    setOrderNumber(orderRes.order_number);
                    clearCart();
                    setStage("done");
                    toast.success("Payment successful");
                } catch (err) {
                    setError(
                        err?.response?.data?.detail ||
                            "Payment received but verification failed. Please contact support with your payment ID.",
                    );
                } finally {
                    setSubmitting(false);
                }
            },
            modal: {
                ondismiss: async () => {
                    setSubmitting(false);
                    try {
                        await axios.post(`${API}/razorpay/cancel`, {
                            internal_order_id: orderRes.internal_order_id,
                            reason: "user_dismissed",
                        });
                    } catch {
                        /* ignore */
                    }
                    toast("Payment cancelled. Your cart is still saved.", { icon: "↩️" });
                },
            },
        };

        const rzp = new window.Razorpay(options);
        rzp.on("payment.failed", async (resp) => {
            setSubmitting(false);
            setError(
                resp?.error?.description ||
                    "Payment failed. You can try again or use a different method.",
            );
            try {
                await axios.post(`${API}/razorpay/cancel`, {
                    internal_order_id: orderRes.internal_order_id,
                    reason: resp?.error?.reason || "payment_failed",
                });
            } catch {
                /* ignore */
            }
        });
        rzp.open();
    };

    return (
        <>
            <div
                onClick={close}
                className={`fixed inset-0 bg-sun-ink/40 backdrop-blur-sm z-50 transition-opacity duration-500 ${
                    open ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
            />
            <aside
                data-testid="cart-drawer"
                className={`fixed top-0 right-0 h-full w-full sm:w-[460px] md:w-[520px] bg-sun-bg text-sun-ink z-50 transition-transform duration-700 flex flex-col ${
                    open ? "translate-x-0" : "translate-x-full"
                }`}
            >
                <div className="flex items-center justify-between h-20 px-6 border-b border-sun-line">
                    <div>
                        <div className="overline">— {stage === "cart" ? "Your cart" : stage === "checkout" ? "Checkout" : "Order received"}</div>
                        <div className="font-heading text-2xl">
                            {stage === "cart" && `${items.length} ${items.length === 1 ? "line" : "lines"}`}
                            {stage === "checkout" && "Shipping details"}
                            {stage === "done" && orderNumber}
                        </div>
                    </div>
                    <button
                        onClick={close}
                        data-testid="close-cart-button"
                        className="w-11 h-11 flex items-center justify-center rounded-full border border-sun-ink/20 hover:bg-sun-ink hover:text-sun-bg transition-colors"
                        aria-label="Close cart"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {stage === "cart" && (
                        <div className="p-6">
                            {items.length === 0 ? (
                                <div data-testid="cart-empty" className="text-center py-20">
                                    <div className="w-20 h-20 mx-auto rounded-full bg-sunrise text-white flex items-center justify-center mb-6">
                                        <span className="font-heading text-3xl">✦</span>
                                    </div>
                                    <div className="font-heading text-3xl mb-3 text-sun-ink">Empty cart.</div>
                                    <p className="text-sun-inkSoft text-sm mb-8">
                                        Pick a few powders from the catalogue to get started.
                                    </p>
                                    <button onClick={close} className="btn-outline">
                                        Browse catalogue
                                    </button>
                                </div>
                            ) : (
                                <ul className="divide-y divide-sun-line">
                                    {items.map((it) => (
                                        <li
                                            key={`${it.product_id}_${it.pack_size}`}
                                            data-testid={`cart-line-${it.product_id}-${it.pack_size}`}
                                            className="flex gap-4 py-5"
                                        >
                                            <div className="w-20 h-24 bg-white overflow-hidden flex-shrink-0 rounded-2xl border border-sun-line">
                                                <img src={it.image} alt={it.product_name} className="w-full h-full object-cover" />
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex justify-between gap-3">
                                                    <div>
                                                        <div className="font-heading text-lg leading-tight text-sun-ink">{it.product_name}</div>
                                                        <div className="overline mt-1">{it.pack_size} · ₹{it.unit_price}</div>
                                                    </div>
                                                    <button
                                                        onClick={() => removeItem(it.product_id, it.pack_size)}
                                                        data-testid={`remove-${it.product_id}-${it.pack_size}`}
                                                        className="text-sun-muted hover:text-sun-orange transition-colors"
                                                        aria-label="remove line"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                                <div className="mt-3 flex items-center justify-between">
                                                    <div className="flex items-center bg-sun-cream rounded-full">
                                                        <button
                                                            onClick={() => updateQuantity(it.product_id, it.pack_size, it.quantity - 1)}
                                                            className="w-8 h-8 flex items-center justify-center text-sun-inkSoft hover:text-sun-orange rounded-full"
                                                            data-testid={`cart-minus-${it.product_id}-${it.pack_size}`}
                                                        >
                                                            <Minus className="w-3 h-3" />
                                                        </button>
                                                        <span className="w-7 text-center font-mono text-xs text-sun-ink font-semibold">{it.quantity}</span>
                                                        <button
                                                            onClick={() => updateQuantity(it.product_id, it.pack_size, it.quantity + 1)}
                                                            className="w-8 h-8 flex items-center justify-center text-sun-inkSoft hover:text-sun-orange rounded-full"
                                                            data-testid={`cart-plus-${it.product_id}-${it.pack_size}`}
                                                        >
                                                            <Plus className="w-3 h-3" />
                                                        </button>
                                                    </div>
                                                    <div className="font-mono text-sm text-sun-ink font-semibold">
                                                        ₹{(it.unit_price * it.quantity).toLocaleString("en-IN")}
                                                    </div>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    {stage === "checkout" && (
                        <form onSubmit={payWithRazorpay} data-testid="checkout-form" className="p-6 space-y-5">
                            <Field label="Full name *" name="customer_name" value={form.customer_name} onChange={onChange} required />
                            <div className="grid grid-cols-2 gap-4">
                                <Field label="Email *" name="email" type="email" value={form.email} onChange={onChange} required />
                                <Field label="Phone *" name="phone" value={form.phone} onChange={onChange} required />
                            </div>
                            <Field label="Address line 1 *" name="address_line1" value={form.address_line1} onChange={onChange} required />
                            <Field label="Address line 2" name="address_line2" value={form.address_line2} onChange={onChange} />
                            <div className="grid grid-cols-2 gap-4">
                                <Field label="City *" name="city" value={form.city} onChange={onChange} required />
                                <Field label="State *" name="state" value={form.state} onChange={onChange} required />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <Field label="Postal code *" name="postal_code" value={form.postal_code} onChange={onChange} required />
                                <Field label="Country *" name="country" value={form.country} onChange={onChange} required />
                            </div>
                            <div>
                                <label className="overline block mb-2">— Notes</label>
                                <textarea
                                    name="notes"
                                    value={form.notes}
                                    onChange={onChange}
                                    rows={2}
                                    data-testid="checkout-notes"
                                    className="w-full bg-transparent border-0 border-b border-sun-line focus:outline-none focus:border-sun-orange px-0 py-2 font-body text-sun-ink resize-none text-sm"
                                />
                            </div>
                            {error && (
                                <div data-testid="checkout-error" className="border border-red-300 bg-red-50 text-red-700 px-4 py-3 text-sm rounded-xl">
                                    {error}
                                </div>
                            )}
                        </form>
                    )}

                    {stage === "done" && (
                        <div className="p-8 text-center" data-testid="order-success">
                            <div className="mx-auto w-16 h-16 rounded-full bg-sunrise text-white flex items-center justify-center">
                                <Check className="w-7 h-7" strokeWidth={2} />
                            </div>
                            <h3 className="mt-6 font-heading text-3xl text-sun-ink">Payment received.</h3>
                            <p className="mt-3 text-sun-inkSoft text-sm">
                                Reference <span className="font-mono text-sun-ink">{orderNumber}</span>.
                                We'll email tracking within 24 hours.
                            </p>
                            <button onClick={close} className="mt-8 btn-outline">
                                Continue browsing
                            </button>
                        </div>
                    )}
                </div>

                {stage !== "done" && items.length > 0 && (
                    <div className="border-t border-sun-line p-6 pb-24 md:pb-20 space-y-3 bg-sun-cream">
                        <div className="flex justify-between items-center font-mono text-sm">
                            <span className="overline">Subtotal</span>
                            <span data-testid="cart-subtotal" className="text-sun-ink font-semibold">
                                ₹{subtotal.toLocaleString("en-IN")}
                            </span>
                        </div>
                        {discount > 0 && (
                            <div
                                data-testid="cart-coupon-line"
                                className="flex justify-between items-center bg-white rounded-xl p-3"
                            >
                                <div className="flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-full bg-sun-ink text-sun-gold flex items-center justify-center font-mono text-[10px] font-bold">
                                        %
                                    </span>
                                    <div className="leading-tight">
                                        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-sun-ink font-semibold">
                                            {coupon.code} applied
                                        </div>
                                        <div className="font-mono text-[10px] text-sun-muted">
                                            10% off — auto applied
                                        </div>
                                    </div>
                                </div>
                                <span className="text-sun-orange font-semibold font-mono text-sm">
                                    −₹{discount.toLocaleString("en-IN")}
                                </span>
                            </div>
                        )}
                        <div className="flex justify-between items-center pt-2 border-t border-sun-ink/15">
                            <span className="font-mono text-xs uppercase tracking-[0.22em] text-sun-ink font-medium">
                                You pay
                            </span>
                            <span data-testid="cart-total" className="font-heading text-2xl text-sun-ink">
                                ₹{total.toLocaleString("en-IN")}
                            </span>
                        </div>
                        <p className="text-xs text-sun-inkSoft pt-1">
                            Shipping & taxes calculated after dispatch confirmation. Pay securely with UPI, card or net-banking.
                        </p>
                        {stage === "cart" ? (
                            <button
                                onClick={() => setStage("checkout")}
                                data-testid="goto-checkout-button"
                                className="btn-gradient w-full"
                            >
                                Checkout
                                <ArrowUpRight className="w-4 h-4" />
                            </button>
                        ) : (
                            <div className="flex gap-3">
                                <button onClick={() => setStage("cart")} className="btn-outline flex-1" data-testid="back-to-cart-button">
                                    Back
                                </button>
                                <button
                                    type="button"
                                    onClick={() => payWithRazorpay()}
                                    disabled={submitting || !razorpayReady}
                                    data-testid="place-order-button"
                                    className="btn-gradient flex-1 disabled:opacity-50"
                                >
                                    {submitting
                                        ? "Opening Razorpay…"
                                        : razorpayReady
                                        ? `Pay ₹${total.toLocaleString("en-IN")}`
                                        : "Loading payment…"}
                                </button>
                            </div>
                        )}
                        <div className="flex items-center justify-center gap-2 pt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-sun-muted">
                            <ShieldCheck className="w-3 h-3" strokeWidth={2} />
                            Secure checkout · Powered by Razorpay · UPI · Card · Netbanking
                        </div>
                    </div>
                )}
            </aside>
        </>
    );
};

const Field = ({ label, ...props }) => (
    <div>
        <label className="overline block mb-2">— {label}</label>
        <input
            {...props}
            data-testid={`checkout-input-${props.name}`}
            className="w-full bg-transparent border-0 border-b border-sun-line focus:outline-none focus:border-sun-orange px-0 py-2 font-body text-sun-ink"
        />
    </div>
);

export default CartDrawer;
